import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import { Shield, Key, AlertTriangle, Trash2, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [resetting, setResetting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'last_login', direction: 'desc' });

  // Sort users based on current sort configuration
  const sortedUsers = useMemo(() => {
    const sorted = [...users];
    sorted.sort((a, b) => {
      let aValue, bValue;
      
      switch (sortConfig.key) {
        case 'is_admin':
          aValue = a.is_admin ? 1 : 0;
          bValue = b.is_admin ? 1 : 0;
          break;
        case 'username':
          aValue = a.username?.toLowerCase() || '';
          bValue = b.username?.toLowerCase() || '';
          break;
        case 'created_at':
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
          break;
        case 'last_login':
          aValue = a.last_login ? new Date(a.last_login).getTime() : new Date(a.created_at).getTime();
          bValue = b.last_login ? new Date(b.last_login).getTime() : new Date(b.created_at).getTime();
          break;
        case 'total_tasks':
          aValue = a.total_tasks || 0;
          bValue = b.total_tasks || 0;
          break;
        case 'total_tags':
          aValue = a.total_tags || 0;
          bValue = b.total_tags || 0;
          break;
        default:
          return 0;
      }
      
      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [users, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown size={14} className="ml-1 opacity-50" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp size={14} className="ml-1" />
      : <ArrowDown size={14} className="ml-1" />;
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/users`, { withCredentials: true });
      setUsers(response.data.users);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleAdmin = async (userId, currentStatus) => {
    try {
      const response = await axios.patch(
        `${API}/admin/users/${userId}/admin`,
        {},
        { withCredentials: true }
      );
      
      toast.success(`Admin status updated for ${response.data.username}`);
      fetchUsers(); // Refresh list
    } catch (error) {
      console.error('Error toggling admin:', error);
      toast.error(error.response?.data?.detail || 'Failed to update admin status');
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setResetting(true);
    try {
      const response = await axios.post(
        `${API}/admin/users/${selectedUser.id}/reset-password`,
        { new_password: newPassword },
        { withCredentials: true }
      );
      
      toast.success(response.data.message);
      setShowResetDialog(false);
      setNewPassword('');
      setSelectedUser(null);
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setResetting(false);
    }
  };

  const handleDeleteUser = async () => {
    setDeleting(true);
    try {
      const response = await axios.delete(
        `${API}/admin/users/${selectedUser.id}`,
        { withCredentials: true }
      );
      
      toast.success(response.data.message);
      setShowDeleteDialog(false);
      setSelectedUser(null);
      fetchUsers(); // Refresh list
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="text-center">Loading...</div>
      </main>
    );
  }

  return (
    <main className="pb-6 sm:pb-8" data-testid="admin-page">
      {/* Header - full width background */}
      <div className="sticky top-[60px] z-20 bg-slate-900">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-2 md:pt-6 pb-4">
          <header>
            <div className="flex items-center gap-3 mb-2">
              <Shield className="text-red-500" size={32} />
              <h1 className="h-font text-2xl md:text-3xl font-semibold text-white">Admin Panel</h1>
            </div>
            <p className="text-sm text-muted-foreground">
              Manage users and admin privileges.
            </p>
            <p className="text-sm text-muted-foreground">
              Total users: {users.length} | Active users (7 days): {
                users.filter(u => {
                  if (!u.last_login) return false;
                  const sevenDaysAgo = new Date();
                  sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
                  sevenDaysAgo.setHours(0, 0, 0, 0);
                  return new Date(u.last_login) >= sevenDaysAgo;
                }).length
              }
            </p>
          </header>
        </div>
      </div>

      {/* Users Table */}
      <div className="w-[960px] mx-auto px-4 sm:px-6 mt-6">
        <div className="rounded-md border overflow-x-auto">
          <Table className="w-full">
            <TableHeader>
              <TableRow>
                <TableHead 
                  className="text-center pl-4 cursor-pointer hover:bg-muted/50 select-none" 
                  style={{ width: '80px' }}
                  onClick={() => handleSort('is_admin')}
                >
                  <div className="flex items-center justify-center">
                    Admin
                    <SortIcon columnKey="is_admin" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-muted/50 select-none" 
                  style={{ width: '180px' }}
                  onClick={() => handleSort('username')}
                >
                  <div className="flex items-center">
                    Username
                    <SortIcon columnKey="username" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-muted/50 select-none" 
                  style={{ width: '100px' }}
                  onClick={() => handleSort('created_at')}
                >
                  <div className="flex items-center">
                    Created
                    <SortIcon columnKey="created_at" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-muted/50 select-none" 
                  style={{ width: '120px' }}
                  onClick={() => handleSort('last_login')}
                >
                  <div className="flex items-center">
                    Login
                    <SortIcon columnKey="last_login" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-muted/50 select-none" 
                  style={{ width: '70px' }}
                  onClick={() => handleSort('total_tasks')}
                >
                  <div className="flex items-center">
                    Tasks
                    <SortIcon columnKey="total_tasks" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-muted/50 select-none" 
                  style={{ width: '70px' }}
                  onClick={() => handleSort('total_tags')}
                >
                  <div className="flex items-center">
                    Tags
                    <SortIcon columnKey="total_tags" />
                  </div>
                </TableHead>
                <TableHead></TableHead>
                <TableHead className="pr-4" style={{ width: '280px' }}>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedUsers.map((u) => (
                <TableRow key={u.id}>
                  <TableCell className="text-center pl-4" style={{ width: '80px' }}>
                    <div className="flex items-center justify-center">
                      <Switch
                        checked={u.is_admin || false}
                        onCheckedChange={() => handleToggleAdmin(u.id, u.is_admin)}
                        disabled={u.username === user?.username}
                      />
                    </div>
                  </TableCell>
                  <TableCell className="font-medium" style={{ width: '180px' }}>
                    <div className="flex items-center gap-2">
                      {u.username}
                      {u.username === user?.username && (
                        <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded">
                          You
                        </span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm" style={{ width: '100px' }}>
                    {new Date(u.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm" style={{ width: '120px' }}>
                    {new Date(u.last_login || u.created_at).toLocaleDateString()}
                  </TableCell>
                  <TableCell className="text-muted-foreground" style={{ width: '70px' }}>
                    {u.total_tasks || 0}
                  </TableCell>
                  <TableCell className="text-muted-foreground" style={{ width: '70px' }}>
                    {u.total_tags || 0}
                  </TableCell>
                  <TableCell></TableCell>
                  <TableCell className="pr-4" style={{ width: '280px' }}>
                    <div className="flex items-center justify-start gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedUser(u);
                          setShowResetDialog(true);
                        }}
                      >
                        <Key size={14} className="mr-1" />
                        Reset Password
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => {
                          setSelectedUser(u);
                          setShowDeleteDialog(true);
                        }}
                        disabled={u.username === user?.username}
                        className="hover:text-black"
                      >
                        <Trash2 size={14} className="mr-1" />
                        Delete
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Reset Password Dialog */}
      <Dialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="text-amber-500" size={20} />
              Reset Password
            </DialogTitle>
            <DialogDescription>
              Set a new password for <strong>{selectedUser?.username}</strong>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="text"
                placeholder="Enter new password (min 6 characters)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={resetting}
              />
              <p className="text-xs text-muted-foreground">
                This will immediately change the user&apos;s password.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowResetDialog(false);
                setNewPassword('');
                setSelectedUser(null);
              }}
              disabled={resetting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleResetPassword}
              disabled={resetting || !newPassword || newPassword.length < 6}
            >
              {resetting ? 'Resetting...' : 'Reset Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="text-red-500" size={20} />
              Delete User Account
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{selectedUser?.username}</strong>?
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-red-950/20 border border-red-800 rounded-md p-4">
              <p className="text-sm text-red-200 font-medium">
                ⚠️ This action cannot be undone!
              </p>
              <p className="text-xs text-red-300 mt-2">
                This will permanently delete:
              </p>
              <ul className="text-xs text-red-300 mt-1 ml-4 list-disc">
                <li>User account</li>
                <li>All tasks</li>
                <li>All tags</li>
                <li>All settings</li>
                <li>All active sessions</li>
              </ul>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteDialog(false);
                setSelectedUser(null);
              }}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteUser}
              disabled={deleting}
              className="hover:text-black"
            >
              {deleting ? 'Deleting...' : 'Delete User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}
